import {
  Boxes,
  CalendarDays,
  ClipboardCheck,
  FileText,
  Laptop,
  PackagePlus,
  Paperclip,
  Presentation,
  ReceiptText,
  Stethoscope,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { formatMinutes } from "@altek/ui/lib/format/number";
import { ASSET_KIND_LABELS } from "@/features/assets/lib/asset-labels";
import { returnDayText } from "@/features/leave/lib/leave-return";
import { env } from "@/lib/env";
import { humanDate, humanRange } from "@/lib/time/human-date";

/**
 * How an approval row describes the thing it is about.
 *
 * THE PROBLEM THIS SOLVES. An approval instance knows its subject as a type string and a UUID.
 * An inbox rendered from that says "hr.leave_request · 01a084…", which is not something anybody
 * can triage — the approver cannot tell a one-day errand from a fortnight in August without
 * opening every row. The engine already records a CONTEXT SNAPSHOT when the flow starts, and
 * for leave that snapshot holds the request number, the dates and the calculated minutes. This
 * file turns those snapshots into a line of text.
 *
 * A REGISTRY RATHER THAN A SWITCH, because the point of a generic engine is that a new module
 * gains approvals without the approval screen being rewritten. An unregistered type degrades
 * to its own name and a note — visibly incomplete rather than silently blank, so it is noticed
 * and added.
 *
 * DETAY ARTIK ONAY SAYFASININ KENDİSİNDE (15 Eylül 2026). Satırın başlığı eskiden doğrudan
 * modülün ekranına bağlanıyordu (`/hr/leave-requests?kayit=…`) ve bu iki şeyi birden bozuyordu:
 *
 *   Bağlantı, onaylayanın AÇAMAYACAĞI bir ekrana gidiyordu. Roller alanlarına kapatılınca
 *   (satışçı sadece satış) bir satış müdürünün `hr.leave.read` anahtarı kalmadı — oysa astının
 *   iznini onaylamak hâlâ onun işi. «Basınca detayını görebilecekler» şartı, detayın başka bir
 *   alanın ekranında durmasıyla bağdaşmıyordu.
 *
 *   Stok belgesinin bağlantısı zaten ÖLÜYDÜ: `/stok/belgeler` diye bir rota yok (adresler
 *   İngilizceye döndüğünde bu satır atlanmıştı, çünkü rota çiti ROTA TABLOSUNU tarıyor, kod
 *   içindeki bağlantı metinlerini değil — o boşluk `route-language.test.ts`'te kapatıldı).
 *
 * Bunun yerine her tanımlayıcı, anlık görüntüden okunan ALAN LİSTESİ (`facts`) veriyor; onay
 * sayfası bunu kendi çekmecesinde gösteriyor ve hiçbir alan anahtarı gerektirmiyor. Modülün
 * ekranına giden bağlantı `record` olarak duruyor ama artık YETKİSİYLE BİRLİKTE: yalnız o
 * anahtarı taşıyan kişiye gösteriliyor, yani kimse açamayacağı bir bağlantı görmüyor.
 */

export interface SubjectFact {
  label: string;
  value: string;
}

export interface SubjectDescription {
  /** What the row's first column says. */
  title: string;
  /** A second line: dates, amounts, a unit. */
  detail?: string;
  /**
   * Onay KARTININ ilk bilgi satırı — kararın ilk sorusunun cevabı (31 Ekim 2026; izinde «Tarih:
   * 16–18.11.2026 · 3 gün»). Verilen türde kart anlık görüntünün öteki alanlarını yazmıyor: bu satır
   * onları özetliyor, gerisi çekmecede.
   */
  lead?: SubjectFact;
  /**
   * Anlık görüntüden okunan alanlar — onay çekmecesinin gövdesi.
   *
   * Yalnız akış başlarken KAYDEDİLMİŞ olan. Burada bir isim ya da ürün adı yoksa sebebi
   * anlık görüntünün onu taşımamasıdır; uydurmak yerine satır hiç yazılmıyor.
   */
  facts?: SubjectFact[];
  /**
   * Modülün kendi ekranı, ve onu açmak için gereken anahtar.
   *
   * `permission` OLMADAN gösterilmemeli: onaylayan kişi çoğu zaman o alanın dışından ve
   * bağlantı ona 403 verirdi. Menüdeki gibi «başkasının kaydı» sorusu sorulduğu için çağıran
   * taraf `canForOthers` ile bakıyor.
   */
  record?: { href: string; permission: string };
  /**
   * Kaydın DOSYASI, onaycı için (ik-belge 20 Ekim 2026): bir özlük belgesine karar veren birim başı İK
   * ekranlarını açamaz ve açmamalı — kararı dosyaya bakarak vermesi için sunucu, açık görevi olan onaycıya
   * dosyanın kendisini (`GET …/file`) açıyor. `record`ın aksine yetki anahtarına bağlı DEĞİL: kapıyı sunucu
   * (görevin kendisi) tutuyor; görev bitince dosya da kapanıyor.
   */
  file?: { href: string; label: string };
}

type Describer = (context: Record<string, unknown>, subjectId: string) => SubjectDescription;

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value : null;

const number = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** Değeri olmayan satır hiç yazılmaz — boş bir etiket «bilinmiyor» değil, «yok» diye okunur. */
const fact = (label: string, value: string | null): SubjectFact[] =>
  value === null || value === "" ? [] : [{ label, value }];

/**
 * Tarih satırı — ve ÖNCE ham değere bakılıyor.
 *
 * `formatDate(null)` bir tire döndürüyor, boş dize değil; doğrudan `fact`'e verilseydi anlık
 * görüntüde olmayan bir tarih «Başlangıç —» diye bir satır olarak çizilirdi. Yani eksik veri,
 * eksik olduğunu göstermek yerine boş bir alan gibi görünürdü.
 */
const dateFact = (label: string, raw: unknown): SubjectFact[] => {
  const value = text(raw);
  return value === null ? [] : fact(label, humanDate(value));
};

/** Sunum biçimi — sunum talebinin anlık görüntüsü kodu taşıyor, ekran adını yazıyor. */
const PRESENTATION_FORMAT_LABELS: Record<string, string> = {
  IN_PERSON: "Yüz yüze",
  ONLINE: "Çevrim içi",
};

/** `detail` yalnız bir değer varsa yazılır — boş ikinci satır «bilgi yok» değil, «boşluk» okunur. */
/** Zimmet talebinin iki türü (30 Ekim 2026) — `asset-requests/lib/asset-request-labels.ts` ile aynı kelimeler. */
const ASSET_REQUEST_KIND_LABELS: Record<string, string> = { DAMAGE: "Hasar", REPLACEMENT: "Değişim" };

/** Sevkiyatın yönü — sunucunun `jobDirection` sözlüğü (`operations.schema.ts`). */
const JOB_DIRECTION_LABELS: Record<string, string> = {
  INBOUND: "Gelen",
  OUTBOUND: "Giden",
  TRANSFER: "Aktarma",
};

const withDetail = (detail: string | null): { detail?: string } => (detail ? { detail } : {});

/** «Örnek Hastanesi, Ankara»; kurumun adı şehri zaten söylüyorsa («Ankara Şehir Hastanesi») şehir bir daha yazılmaz. */
function institutionWithCity(institution: string | null, city: string | null): string | null {
  if (!institution) return city;
  if (!city || institution.toLocaleLowerCase("tr-TR").includes(city.toLocaleLowerCase("tr-TR"))) return institution;
  return `${institution}, ${city}`;
}

/** Boş parçaları atıp ` · ` ile birleştirir; hiçbiri yoksa satır hiç yazılmaz. */
const joinParts = (...parts: Array<string | null>): string | null => {
  const present = parts.filter((part): part is string => part !== null && part !== "");
  return present.length === 0 ? null : present.join(" · ");
};

const DESCRIBERS: Record<string, Describer> = {
  "hr.leave_request": (context, subjectId) => {
    const requestNo = text(context.requestNo);
    const range = humanRange(text(context.startDate), text(context.endDate));
    /**
     * SÜRE GÜN OLARAK (ik-duzeltme 19 Ekim 2026, İK-19). Satır dakikadan «16 sa» yazıyordu;
     * çalışanın ekranı aynı izne «2 gün» diyor. Sunucu artık günü kişinin günlük çalışma süresiyle
     * çevirip anlık görüntüye yazıyor (`calculatedDays`, «1.5»). Bundan önce başlamış akışlarda
     * gün yok — o satırlar eskisi gibi saatle okunuyor.
     */
    const days = text(context.calculatedDays);
    const minutes = number(context.calculatedMinutes);
    const duration =
      days !== null ? `${days.replace(".", ",")} gün` : minutes !== null ? formatMinutes(minutes) : null;
    const typeName = text(context.leaveTypeName);
    /*
      31 Ekim 2026 (Onay Merkezi, patronla tarayıcı turu): kartın ilk satırı TARİH ve GÜN — «ne zaman,
      kaç gün». Tür başlığa geçti («Yıllık izin»); talep numarası çekmecede. Türü olmayan eski anlık
      görüntüde başlık eskisi gibi «İzin talebi IZN-…».
    */
    const span = joinParts(humanRange(text(context.startDate), text(context.endDate)), duration);
    return {
      title: typeName ?? (requestNo ? `İzin talebi ${requestNo}` : "İzin talebi"),
      // Kişinin adı yazılmıyor: «Talep eden» onu zaten söylüyor (izin kişinin kendi hesabından
      // açılıyor; `employeeName` onaycı bildiriminin cümlesi için anlık görüntüde).
      detail: span ?? range,
      ...(span ? { lead: { label: "Tarih", value: span } } : {}),
      facts: [
        ...fact("Talep no", requestNo),
        ...fact("İzin türü", text(context.leaveTypeName)),
        ...dateFact("Başlangıç", context.startDate),
        ...dateFact("Bitiş", context.endDate),
        ...fact("Süre", duration),
        /*
          31 Ekim 2026 (Onay Merkezi): «kişi ne zaman işte». Sunucu talep anında kişinin kendi
          takvimiyle bulup anlık görüntüye yazıyor; bu tarihten önceki akışlarda yok, satır çizilmiyor.
        */
        ...fact(
          "İşe dönüş",
          returnDayText(
            text(context.returnDate),
            text(context.returnDayPart) === "SECOND_HALF" ? "SECOND_HALF" : "FULL",
          ),
        ),
      ],
      // The HR list, not the employee's own: whoever is approving is by definition not the
      // requester, so their own leave screen would show them nothing.
      // ik-izin 6 Ekim 2026: the tab address and the key the list reads (`record`, was `kayit`).
      record: { href: `/hr/leave/requests?record=${subjectId}`, permission: "hr.leave.read" },
    };
  },

  "hr.medical_report": (context, subjectId) => ({
    title: "Sağlık raporu",
    detail: humanRange(text(context.startDate), text(context.endDate)),
    /**
     * TEŞHİS YOK, ve olmadığı için gösterilmiyor: sistem hekim notu ve teşhis kaydetmiyor.
     * Sağlık verisi KVKK özel nitelikli; onay çekmecesinde de yalnız tarih ve tür var.
     */
    facts: [
      ...fact("Tür", text(context.reportTypeCode)),
      ...dateFact("Başlangıç", context.startDate),
      ...dateFact("Bitiş", context.endDate),
    ],
    // ik-izin 6 Ekim 2026: reports live in the leave section's «Raporlar» tab, and the drawer
    // opens on `panel=edit&record=` (`useResourcePanel`) — `?kayit=` opened the list only.
    record: {
      href: `/hr/leave/reports?panel=edit&record=${subjectId}`,
      permission: "hr.medical_report.read",
    },
  }),

  /**
   * ÖZLÜK BELGESİ — ik-belge 20 Ekim 2026. Kendi belgesini yükleyen İK'nın belgesine, şirkette başka İK
   * yetkilisi yoksa birim başı → üst birim başı bakıyor (`hr.employee_document.review`). Onaycı İK
   * ekranlarını göremez; çekmece anlık görüntünün taşıdığı her şeyi yazıyor ve belgenin DOSYASINI açan
   * bağlantıyı veriyor. Dosya adı ve not anlık görüntüde YOK (kişisel veri taşıyabilir) — yalnız ad ve tür.
   */
  "hr.employee_document": (context, subjectId) => {
    const typeName = text(context.documentTypeName);
    return {
      title: joinParts("Özlük belgesi", typeName) ?? "Özlük belgesi",
      // Çalışanın adı YAZILMIYOR: bu zincir yalnız kendi belgesini yükleyen İK için açılır, yani «Talep eden»
      // zaten belgenin sahibi — ikinci bir «Çalışan» satırı aynı kişiyi iki kez söylerdi.
      facts: [
        ...fact("Belge türü", typeName),
        ...dateFact("Veriliş", context.issuedAt),
        ...dateFact("Geçerlilik bitişi", context.expiresAt),
      ],
      file: {
        href: `${env.apiBaseUrl}/hr/employee-documents/${encodeURIComponent(subjectId)}/file`,
        label: "Belgeyi görüntüle",
      },
    };
  },

  /**
   * STOK BELGESİ İPTALİ (31 Ekim 2026, UI/UX denetimi İ-A7). Akışa yalnız İPTAL talebi giriyor — kayıtlı
   * belgenin geri alınması — ama satır bunu söylemiyordu ve gerekçe (anlık görüntüde var) hiç çizilmiyordu;
   * onaycı neyi onayladığını bilmeden karar veriyordu. Belge türü anlık görüntüde yok, uydurulmuyor.
   *
   * BAĞLANTI ÖLÜYDÜ: `/inventory/documents?kayit=` bir yönlendirmeye düşüp parametresini kaybediyordu;
   * belgenin çekmecesi `/inventory/movements` sekmesinde `panel=edit&record=` ile açılıyor.
   */
  "inventory.document": (context, subjectId) => {
    const documentNo = text(context.documentNo);
    const reason = text(context.reason);
    return {
      title: documentNo ? `Stok belgesi iptali ${documentNo}` : "Stok belgesi iptali",
      detail: joinParts("İptal talebi", reason) ?? "İptal talebi",
      facts: [
        ...fact("Belge no", documentNo),
        ...fact("İstenen", "Kayıtlı belgenin iptali"),
        ...fact("Gerekçe", reason),
      ],
      record: {
        href: `/inventory/movements?panel=edit&record=${encodeURIComponent(subjectId)}`,
        permission: "inventory.document.read",
      },
    };
  },

  /**
   * SEVKİYAT İPTALİ (31 Ekim 2026, İ-A6): başlıkta ham anahtar («operations.transport_job») yazıyordu ve
   * tür filtrede yoktu. Anlık görüntü iş numarasını, yönü, durumu ve gerekçeyi taşıyor
   * (`operations.jobs.service.ts`); sahanın adı yok, kimliği yazılmıyor.
   */
  "operations.transport_job": (context, subjectId) => {
    const jobNo = text(context.jobNo);
    const direction = text(context.direction);
    const reason = text(context.reason);
    return {
      title: jobNo ? `Sevkiyat iptali ${jobNo}` : "Sevkiyat iptali",
      detail: joinParts(direction ? (JOB_DIRECTION_LABELS[direction] ?? direction) : null, reason) ?? "İptal talebi",
      facts: [
        ...fact("İş no", jobNo),
        ...fact("Yön", direction ? (JOB_DIRECTION_LABELS[direction] ?? direction) : null),
        ...fact("İstenen", "Sevkiyatın iptali"),
        ...fact("Gerekçe", reason),
      ],
      record: {
        // Sevkiyat takibi (1 Kasım 2026): iptali onaylayan işin bütün kaydını görsün — kim yükledi, ne
        // zaman yola çıktı. Planlama ekranı tesis/araç/sürücü okuması da istiyor; onaycı onları taşımayabilir.
        href: `/operations/shipment-tracking?panel=edit&record=${encodeURIComponent(subjectId)}`,
        permission: "operations.transport_job.read",
      },
    };
  },

  /**
   * DOSYA ONAYI (31 Ekim 2026, İ-A6): bir kayda eklenen dosyanın onayı. Anlık görüntü dosyanın ADINI ve
   * amacını taşıyor, baytlarını değil (`files.service.ts`); hangi kayda eklendiği türün adıyla yazılıyor.
   */
  "files.file_link": (context) => {
    const purpose = text(context.purposeLabel) ?? text(context.purpose);
    const fileName = text(context.fileName);
    const ownerType = text(context.subjectType);
    return {
      title: joinParts("Dosya onayı", purpose) ?? "Dosya onayı",
      ...withDetail(fileName),
      facts: [
        ...fact("Dosya", fileName),
        ...fact("Amaç", purpose),
        ...fact("Eklendiği kayıt", ownerType ? (SUBJECT_TYPE_LABELS[ownerType] ?? ownerType) : null),
      ],
    };
  },

  /**
   * SUNUM TALEBİ — pz-akis 6 Ekim 2026 (plan M15). Bölge müdürü talebi YALNIZ buradan görüyor:
   * pazarlamanın listesini açan anahtar (`events.presentation.read`) onda yok ve olmamalı.
   * O yüzden çekmece, anlık görüntünün taşıdığı her şeyi yazıyor; liste bağlantısı yalnız
   * pazarlama onaylayıcısına çıkıyor.
   */
  "events.presentation_request": (context, subjectId) => {
    const requestNo = text(context.requestNo);
    const topic = text(context.topic);
    const institution = text(context.targetInstitution);
    const requestedOn = text(context.requestedOn);
    const time = text(context.requestedTime);
    const attendees = number(context.expectedAttendees);
    const format = text(context.format);
    return {
      // BAŞLIK NE İSTENDİĞİ, İKİNCİ SATIR NEREDE VE NE ZAMAN (ikinci tasarım turu, 3 Kasım 2026): başlık dört şeyi art arda yazıyordu ve iki satıra sığmıyordu; talep numarası çekmecede.
      title: joinParts("Sunum talebi", topic) ?? "Sunum talebi",
      ...withDetail(joinParts(institutionWithCity(institution, text(context.city)), requestedOn ? humanDate(requestedOn) : null, time)),
      facts: [
        ...fact("Talep no", requestNo),
        ...fact("Konu", topic),
        ...fact("Ürün", joinParts(text(context.productName), text(context.productsText))),
        ...fact("Kurum", institution),
        ...fact("Hekim", text(context.targetPhysicianName)),
        ...fact("Şehir", text(context.city)),
        ...dateFact("Tarih", context.requestedOn),
        ...fact("Saat", time),
        ...fact("Biçim", format ? (PRESENTATION_FORMAT_LABELS[format] ?? format) : null),
        ...fact("Tahmini katılım", attendees !== null ? `${attendees} kişi` : null),
      ],
      record: {
        href: `/presentation-requests?record=${subjectId}`,
        permission: "events.presentation.read",
      },
    };
  },

  /**
   * YENİ ÜRÜN TALEBİ — pz-akis 6 Ekim 2026 (plan M12). Bağlantı KARTA, listeye değil: ruhsat
   * ve depo bölümlerini kartın kendisinde dolduruyor. Anahtarı `workflow.approval.read` —
   * kartın kapısı onu da kabul ediyor, yani satırı gören her onaylayan kartı açabiliyor.
   */
  /**
   * ZİMMET TALEBİ — 30 Ekim 2026 (kullanıcı: «Talep şimdilik patrona gitsin»). Patron kararı burada veriyor;
   * çekmece anlık görüntünün taşıdığı her şeyi yazıyor (kim, hangi eşya, hasar mı değişim mi, ne yazdı).
   * Liste bağlantısı yalnız bütün talepleri okuyana (`hr.asset_request.read` — İK, patron, sistem yöneticisi).
   */
  /**
   * KONGRE ONAYI (1 Kasım 2026): zincir birim başı → patron. Anlık görüntü kongrenin kodu, adı, tarihleri ve
   * şehri; bütçe kongre kartında (kayıt bağlantısı, `events.event.read`).
   */
  "events.event": (context, subjectId) => {
    const name = text(context.name);
    const code = text(context.code);
    const start = text(context.startDate);
    const end = text(context.endDate);
    return {
      title: joinParts("Kongre onayı", name) ?? "Kongre onayı",
      ...withDetail(joinParts(code, start ? humanRange(start, end) : null, text(context.city))),
      facts: [
        ...fact("Kongre", name),
        ...fact("Kod", code),
        ...dateFact("Başlangıç", context.startDate),
        ...dateFact("Bitiş", end),
        ...fact("Şehir", text(context.city)),
      ],
      record: { href: `/events/${subjectId}`, permission: "events.event.read" },
    };
  },

  "hr.asset_request": (context, subjectId) => {
    const requestNo = text(context.requestNo);
    const kind = text(context.kind);
    const kindName = kind ? (ASSET_REQUEST_KIND_LABELS[kind] ?? kind) : null;
    /*
      EŞYANIN TÜRÜ TÜRKÇE (31 Ekim 2026): anlık görüntü türü KOD olarak taşıyor (`LAPTOP`) ve eski talepler hep öyle
      kalacak; ad zimmetin sözlüğünden (`assets/lib/asset-labels.ts`, sunucununkiyle aynı — `hr-asset-kinds.test.ts`).
    */
    const assetKindRaw = text(context.assetKind);
    const assetKind = assetKindRaw ? (ASSET_KIND_LABELS[assetKindRaw] ?? assetKindRaw) : null;
    const asset = joinParts(assetKind, text(context.serialNo));
    return {
      title: joinParts(kindName ? `${kindName} talebi` : "Zimmet talebi", asset) ?? "Zimmet talebi",
      ...withDetail(joinParts(requestNo, text(context.requesterName))),
      facts: [
        ...fact("Talep no", requestNo),
        ...fact("Talep eden", text(context.requesterName)),
        ...fact("Talep türü", kindName),
        ...fact("Eşya", assetKind),
        ...fact("Seri no", text(context.serialNo)),
        ...fact("Eşya açıklaması", text(context.assetDescription)),
        ...dateFact("Zimmet tarihi", context.assignedAt),
        ...fact("Açıklama", text(context.description)),
      ],
      record: { href: `/hr/asset-requests?record=${subjectId}`, permission: "hr.asset_request.read" },
    };
  },

  /**
   * MASRAF BEYANI (finans F2c-1, 2 Kasım 2026; plan §4.3). Anlık görüntüden (`finance.expense.service.ts › submit`)
   * yalnız numara, amaç ve kaçıncı gönderim. TUTAR ANLIK GÖRÜNTÜDEN DEĞİL: ilk adımda reddedilen kalem toplamdan düşüyor
   * ve ikinci adımın onaycısı gönderimdeki tutarı görürse yanlış rakama onay verir (tarayıcıda görüldü: 3.437,50 gönderildi,
   * kalem reddinden sonra 3.377,50). Tutar, kalem, kategori, belge ve kişinin bu ayki toplamı CANLI özetten
   * (`inbox-summary.ts › subjectCardLead`, `expenseClaimDetailFacts`). Kayıt YERİNDE açılıyor
   * (`ApprovalDetailDrawer › IN_PLACE_RECORDS`): onaycı kalemlere ve fişlere bakarak karar veriyor — `record` yok.
   */
  "finance.expense_claim": (context) => {
    const claimNo = text(context.claimNo);
    const title = text(context.title);
    const submission = number(context.submissionNo);
    return {
      title: joinParts("Masraf beyanı", title) ?? "Masraf beyanı",
      ...withDetail(claimNo),
      facts: [
        ...fact("Beyan no", claimNo),
        ...fact("Amaç", title),
        ...fact("Gönderim", submission !== null && submission > 1 ? `${String(submission)}. gönderim (düzeltilip yeniden)` : null),
      ],
    };
  },

  "marketing.product_request": (context, subjectId) => {
    const requestNo = text(context.requestNo);
    const name = text(context.name);
    return {
      title: joinParts("Ürün talebi", name) ?? "Ürün talebi",
      ...withDetail(joinParts(requestNo, text(context.categoryName), text(context.baseUomName))),
      facts: [
        ...fact("Talep no", requestNo),
        ...fact("Ürün adı", name),
        ...fact("Kategori", text(context.categoryName)),
        ...fact("Temel birim", text(context.baseUomName)),
      ],
      record: { href: `/product-requests/${subjectId}`, permission: "workflow.approval.read" },
    };
  },
};

/** Names for the subject types themselves, for the filter and the fallback row. */
export const SUBJECT_TYPE_LABELS: Record<string, string> = {
  "hr.leave_request": "İzin talebi",
  "hr.medical_report": "Sağlık raporu",
  "hr.employee_document": "Özlük belgesi",
  "hr.asset_request": "Zimmet talebi",
  // Akışa yalnız iptal giriyor (31 Ekim 2026) — filtre «Stok belgesi» deyince her belge sanılıyordu.
  "inventory.document": "Stok belgesi iptali",
  "events.presentation_request": "Sunum talebi",
  "events.event": "Kongre onayı",
  "marketing.product_request": "Ürün talebi",
  "operations.transport_job": "Sevkiyat iptali",
  "files.file_link": "Dosya onayı",
  "finance.expense_claim": "Masraf beyanı",
};

/**
 * KARTIN SİMGESİ TÜRÜ SÖYLÜYOR (31 Ekim 2026, İ-A «Detay düğmesi»): her kart aynı onay simgesini
 * taşıyordu ve karışık bir kuyrukta göz, izni ürün talebinden ayırmak için başlığı okumak zorundaydı.
 * Tanımsız tür genel onay simgesine düşüyor.
 */
const SUBJECT_TYPE_ICONS: Record<string, LucideIcon> = {
  "hr.leave_request": CalendarDays,
  "hr.medical_report": Stethoscope,
  "hr.employee_document": FileText,
  "hr.asset_request": Laptop,
  "inventory.document": Boxes,
  "events.presentation_request": Presentation,
  "marketing.product_request": PackagePlus,
  "operations.transport_job": Truck,
  "files.file_link": Paperclip,
  "finance.expense_claim": ReceiptText,
};

export function subjectIcon(subjectType: string): LucideIcon {
  return SUBJECT_TYPE_ICONS[subjectType] ?? ClipboardCheck;
}

export function describeSubject(
  subjectType: string,
  context: Record<string, unknown>,
  subjectId: string,
): SubjectDescription {
  const describer = DESCRIBERS[subjectType];
  if (describer) return describer(context, subjectId);

  return {
    title: SUBJECT_TYPE_LABELS[subjectType] ?? subjectType,
    detail: "Bu kayıt türü için özet tanımlı değil",
  };
}

/** Which subject types the filter offers — the ones this interface can actually describe. */
export function describableSubjectTypes(): Array<{ value: string; label: string }> {
  return Object.keys(DESCRIBERS).map((value) => ({
    value,
    label: SUBJECT_TYPE_LABELS[value] ?? value,
  }));
}
